import { useCallback, useEffect, useMemo, useState } from 'react'
import Card from '@/components/ui/Card'
import { vendorCopy } from '@/pages/teacher/_shop-helpers'
import { supabase } from '@/supabase/client'
import { startProspectShopping } from '@/lib/coreApi'

export type ShoppingSession = {
  id: string
  status: string | null
  metadata: Record<string, unknown> | null
  started_at: string | null
  updated_at: string | null
}

export type ProspectNotes = {
  annual_budget_cents?: number | null
  spent_cents?: number | null
  wallet_credits?: number | null
  shopping_sessions?: ShoppingSession[]
}

export type Prospect = {
  id: number
  name?: string | null
  notes?: ProspectNotes | null
}

type Props = {
  prospect: Prospect | null
  session: { access_token?: string | null } | null
  isCoach: boolean
  loading: boolean
  error: string
}

function formatCurrency(cents?: number | null) {
  if (!Number.isFinite(cents)) return '—'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents as number) / 100)
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatStatus(status = '') {
  if (!status) return '—'
  return status
    .toString()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function computeAvailableCents(notes?: ProspectNotes | null) {
  if (!notes) return null
  const budget = Number(notes.annual_budget_cents)
  const spent = Number(notes.spent_cents)
  if (Number.isFinite(budget) && Number.isFinite(spent)) {
    return Math.max(budget - spent, 0)
  }
  if (Number.isFinite(budget)) return budget
  return null
}

export default function CoachShoppingPanel({ prospect, session, isCoach, loading, error }: Props) {
  const [budgetInput, setBudgetInput] = useState('')
  const [sessions, setSessions] = useState<ShoppingSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsError, setSessionsError] = useState('')
  const [sessionState, setSessionState] = useState<{ loading: boolean; error: string; message: string; session: any | null }>(
    {
      loading: false,
      error: '',
      message: '',
      session: null,
    },
  )

  const availableCents = useMemo(() => computeAvailableCents(prospect?.notes || null), [prospect?.notes])

  const fetchSessions = useCallback(async () => {
    if (!prospect?.id) {
      setSessions([])
      return
    }

    setSessionsLoading(true)
    setSessionsError('')

    const { data, error: loadError } = await supabase
      .from('shopping_sessions')
      .select('id, status, metadata, started_at, updated_at')
      .contains('metadata', { prospect_id: prospect.id })
      .order('started_at', { ascending: false })
      .limit(10)

    if (loadError) {
      setSessionsError(loadError.message || 'Unable to load sessions')
      setSessions([])
    } else {
      setSessions((data || []) as ShoppingSession[])
    }

    setSessionsLoading(false)
  }, [prospect?.id])

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  const startShopping = useCallback(async () => {
    if (!session?.access_token || !prospect?.id) return
    setSessionState({ loading: true, error: '', message: '', session: null })
    const numeric = budgetInput.replace(/[^\d.]/g, '')
    const parsed = numeric ? Number.parseFloat(numeric) : null
    const cents = parsed !== null && Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : null

    try {
      const payload = await startProspectShopping({
        prospect_id: prospect.id,
        estimated_budget_cents: cents || undefined,
      })
      setSessionState({
        loading: false,
        error: '',
        message: 'Your shopping link has been emailed.',
        session: payload?.session || null,
      })
      setBudgetInput('')
      fetchSessions()
    } catch (startError: any) {
      setSessionState({
        loading: false,
        error: startError?.message || 'Unable to start session',
        message: '',
        session: null,
      })
    }
  }, [budgetInput, fetchSessions, prospect?.id, session?.access_token])

  const formattedInput = budgetInput.replace(/[^\d.]/g, '')
  const canStart = Boolean(isCoach && formattedInput && session?.access_token && prospect?.id)

  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-stone-900">Shopping</p>
          <p className="text-sm text-stone-600">Mirror of the teacher shopping flow, tuned for coaches in the locker room.</p>
        </div>
        <div className="rounded-xl border border-sand bg-white px-4 py-3 text-right text-sm text-stone-700">
          <p className="text-xs uppercase tracking-wide text-stone-500">Available balance</p>
          <p className="text-lg font-semibold text-stone-900">{formatCurrency(availableCents)}</p>
        </div>
      </div>

      {loading ? <p className="text-sm text-stone-600">Loading your profile…</p> : null}
      {error ? <p className="text-sm text-brick">{error}</p> : null}

      {prospect ? (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-2 text-sm font-medium text-stone-900">
              Estimated spend (USD)
              <input
                value={formattedInput}
                onChange={(event) => setBudgetInput(event.target.value)}
                placeholder="Optional — e.g. 250"
                inputMode="decimal"
                className="rounded-xl border border-sand bg-white px-3 py-2 text-base text-cocoa focus:border-brick focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brick"
              />
              <span className="text-xs font-normal text-stone-600">We’ll reuse your active privacy card whenever possible.</span>
            </label>
            <div className="rounded-xl bg-stone-50 p-4 text-xs text-stone-700">
              <p className="font-semibold text-stone-900">How it works</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>We confirm budget availability.</li>
                <li>We reuse your active privacy card or create one.</li>
                <li>Confirmation is emailed to your purchase alias.</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={startShopping} disabled={!canStart || sessionState.loading} className="Button primary">
              {sessionState.loading ? 'Starting…' : 'Start session'}
            </button>
            {!isCoach ? (
              <span className="text-sm text-stone-600">Shopping is coach-only. Ask your coach to launch a session.</span>
            ) : null}
          </div>

          {sessionState.error ? <p className="text-sm text-brick">{sessionState.error}</p> : null}
          {sessionState.message ? <p className="text-sm text-emerald-700">{sessionState.message}</p> : null}
          {sessionState.session ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <p className="font-semibold">Session ready</p>
              <p className="mt-1">Alias: {String((sessionState.session as Record<string, unknown>).email_alias || 'sent via email')}</p>
              {(sessionState.session as Record<string, unknown>).card_last4 ? (
                <p className="text-xs text-emerald-800">
                  Card ending in {String((sessionState.session as Record<string, unknown>).card_last4)} (
                  {String((sessionState.session as Record<string, unknown>).card_exp_month)}/{
                    String((sessionState.session as Record<string, unknown>).card_exp_year)
                  }
                  )
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-3 rounded-2xl border border-sand bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-stone-900">Recent shopping sessions</p>
                <p className="text-xs text-stone-600">Sessions created for this prospect will appear here.</p>
              </div>
              <button
                type="button"
                onClick={fetchSessions}
                disabled={sessionsLoading}
                className="rounded-lg border border-sand bg-white px-3 py-2 text-xs font-semibold text-stone-700 shadow-sm hover:bg-shell disabled:cursor-not-allowed disabled:opacity-70"
              >
                {sessionsLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>

            {sessionsError ? <p className="text-sm text-brick">{sessionsError}</p> : null}
            {!sessionsLoading && !sessions.length ? (
              <p className="text-sm text-stone-600">No sessions yet. Start one to receive a purchase alias.</p>
            ) : null}

            <div className="space-y-3">
              {sessions.map((sessionItem) => {
                const meta = (sessionItem?.metadata || {}) as Record<string, unknown>
                const vendorKey = (meta.vendor || meta.partner || '') as string
                const vendorInfo = vendorKey in vendorCopy ? vendorCopy[vendorKey as keyof typeof vendorCopy] : null
                const vendorLabel = vendorInfo?.label || formatStatus(vendorKey || 'Vendor')
                const amount = Number((meta as Record<string, unknown>).amount_cents ?? (meta as Record<string, unknown>).budget_cents)
                const started = sessionItem.started_at || sessionItem.updated_at
                const cardLast4 = (meta as Record<string, unknown>).card_last4
                  ? `•••• ${(meta as Record<string, unknown>).card_last4}`
                  : '—'

                return (
                  <div key={sessionItem.id} className="space-y-3 rounded-2xl border border-sand bg-white p-4 shadow-sm">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-stone-900">{vendorLabel}</p>
                        <p className="text-xs text-stone-600">Started {formatDate(started)}</p>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-shell px-3 py-1 text-xs font-semibold uppercase text-stone-700">
                        {formatStatus(sessionItem.status || undefined)}
                      </span>
                    </div>

                    <div className="grid gap-2 text-sm text-stone-700 sm:grid-cols-2">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-stone-500">Amount</p>
                        <p>{formatCurrency(amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase tracking-wide text-stone-500">Card</p>
                        <p>{cardLast4}</p>
                      </div>
                      {(meta as Record<string, unknown>).alias_email ? (
                        <div className="sm:col-span-2">
                          <p className="text-xs uppercase tracking-wide text-stone-500">Purchase email</p>
                          <p>{(meta as Record<string, unknown>).alias_email as string}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-stone-600">We haven’t linked your athlete profile yet.</p>
      )}
    </Card>
  )
}
